import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { RootState } from '../..';
import { config } from '../../../utils/config';
import { showFeedbackModal } from '../UserFeedbackActions';
import { AppThunk } from '../../types';
import apis from '../../../utils/apis';
import { handleApiError } from '../utils/utils';

interface EngagementLetterData {
  LetterID: string;
  Version: number;
  Content: string;
  HTMLContent: string;
  Status: string;
  ClientResponse: string;
  ClientComment: string;
}

export const uploadFilesForLetter = (files: File[]): AppThunk<Promise<string[]>> => {
    return async dispatch => {
      try {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        const response = await apis.post('/engagement_letter/upload_files', formData);
        if (response.status === 200) {
          return response.data.fileReferences; // Array of file references
        } else {
          handleApiError(response, dispatch);
          return [];
        }
      } catch (err) {
        handleApiError(err, dispatch);
        return [];
      }
    };
  };
  
  export const generateEngagementLetterSSE = (clientId: string, specifications: string, fileReferences: string[]) => {
    return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
      return new Promise<void>((resolve, reject) => {
        dispatch({ type: "START_ENGAGEMENT_LETTER_GENERATION", payload: { clientId } });
        try {
          const baseUrl = `${config.baseUrl}/engagement_letter/generate`;
          const params = new URLSearchParams({
            client_id: clientId,
            specifications,
            fileReferences: JSON.stringify(fileReferences),
          });
          const sseUrl = `${baseUrl}?${params.toString()}`;
  
          let bearerToken = "";
          const rawToken = localStorage.getItem("token") || sessionStorage.getItem("token");
          if (rawToken) {
            bearerToken = JSON.parse(rawToken).token;
          }
  
          const evtSource = new EventSourcePolyfill(sseUrl, {
            headers: { Authorization: `Bearer ${bearerToken}` },
            heartbeatTimeout: 300000,
            withCredentials: true,
          });
  
          let finalData: any = null;
  
          evtSource.onmessage = async (event) => {
            if (!event.data) return;
            let parsed: any;
            try {
              parsed = JSON.parse(event.data);
            } catch (err) {
              console.error("Failed to parse SSE data:", event.data);
              return;
            }
  
            if (parsed.event === "connected") {
              dispatch({
                type: "UPDATE_ENGAGEMENT_LETTER_PROGRESS",
                payload: {
                  clientId,
                  progress: [{ step: "Connecting", status: "Completed", messages: ["Connection established"] }],
                },
              });
            }
  
            if (parsed.progress) {
              dispatch({
                type: "UPDATE_ENGAGEMENT_LETTER_PROGRESS",
                payload: { clientId, progress: parsed.progress },
              });
            }
  
            if (parsed.final) {
              finalData = parsed.final;
              const { letter_id, version, content, html_content } = parsed.final;
              dispatch({
                type: "UPDATE_ENGAGEMENT_LETTER",
                payload: { clientId, letterId: letter_id, version, content, htmlContent: html_content },
              });
              dispatch(
                showFeedbackModal({
                  showModal: true,
                  message: "Engagement letter generated successfully",
                  modalType: "success",
                  duration: 3000,
                })
              );
            }
  
            if (parsed.error) {
              dispatch({
                type: "ENGAGEMENT_LETTER_GENERATION_FAILED",
                payload: { clientId, error: parsed.error },
              });
              evtSource.close();
              reject(parsed.error);
            }
          };
  
          evtSource.addEventListener("done", async () => {
            dispatch({ type: "FINISH_ENGAGEMENT_LETTER_GENERATION", payload: { letterId: finalData?.letter_id } });
            evtSource.close();
            resolve();
            if (finalData) {
              await dispatch(getEngagementLetter(clientId, finalData.letter_id));
            }
          });
  
          evtSource.onerror = (err) => {
            dispatch({
              type: "ENGAGEMENT_LETTER_GENERATION_FAILED",
              payload: { clientId, error: "SSE error - check console." },
            });
            evtSource.close();
            reject("SSE error");
          };
        } catch (err: any) {
          dispatch({
            type: "ENGAGEMENT_LETTER_GENERATION_FAILED",
            payload: { clientId, error: err.toString() },
          });
          reject(err);
        }
      });
    };
  };

  export const getEngagementLetter = (letterId: string, version?: number): AppThunk<Promise<EngagementLetterData | null>> => {
    return async dispatch => {
      try {
        const response = await apis.get('/engagement_letter/get', { params: { letter_id: letterId, version } });
        if (response.status === 200) {
          const letterData = response.data;
          return letterData;
        }
        return null;
      } catch (err) {
        handleApiError(err, dispatch);
        return null;
      }
    };
};

  export const updateEngagementLetter = (letterId: string, version: number, content: string): AppThunk<Promise<void>> => {
    return async dispatch => {
      try {
        const response = await apis.post('/engagement_letter/update', { letter_id: letterId, version, content });
        if (response.status === 200) {
          dispatch({
            type: 'UPDATE_ENGAGEMENT_LETTER',
            payload: { letterId, version, content },
          });
          dispatch(showFeedbackModal({
            modalType: 'success',
            duration: 3000,
            showModal: true,
            message: 'Engagement letter saved',
          }));
        } else {
          handleApiError(response, dispatch);
        }
      } catch (err) {
        handleApiError(err, dispatch);
      }
    };
  };
  
  export const markAsFinal = (clientId: string, version: number, signatureFile: File): AppThunk<Promise<void>> => {
    return async dispatch => {
      try {
        const formData = new FormData();
        formData.append('client_id', clientId);
        formData.append('version', version.toString());
        formData.append('signature', signatureFile);

        const response = await apis.post('/engagement_letter/mark_final', formData);

        if (response.status === 200) {
          dispatch({
            type: 'UPDATE_ENGAGEMENT_LETTER_STATUS',
            payload: { clientId, version, status: 'Final' },
          });
        } else {
          handleApiError(response, dispatch);
        }
      } catch (err) {
        handleApiError(err, dispatch);
      }
    };
};

  export const fetchEngagementLetterVersions = (clientId: string): AppThunk<Promise<any>> => {
    return async dispatch => {
      try {
        const response = await apis.get('/engagement_letter/versions', { params: { client_id: clientId } });
        if (response.status === 200) {
          return response.data;
        } else if (response.status === 404) {
          return [];
        } else {
          handleApiError(response, dispatch);
          return null;
        }
      } catch (err) {
        handleApiError(err, dispatch);
        return null;
      }
    };
  };

  export const respondToEngagementLetter = (
    letterId: string,
    version: number,
    response: 'Accepted' | 'Declined',
    comment?: string,
    signatureFile?: File
  ): AppThunk<Promise<void>> => {
    return async dispatch => {
      try {
        let responseData;
        if (signatureFile) {
          const formData = new FormData();
          formData.append('letter_id', letterId); 
          formData.append('version', version.toString());
          formData.append('response', response);
          if (comment) formData.append('comment', comment);
          formData.append('signature', signatureFile);
          
          responseData = await apis.post('/engagement_letter/respond', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          responseData = await apis.post('/engagement_letter/respond', {
            letter_id: letterId,
            version,
            response,
            comment,
          });
        }
    
        if (responseData.status === 200) {
          dispatch({
            type: 'UPDATE_ENGAGEMENT_LETTER_RESPONSE',
            payload: { letterId, version, response, comment },
          });
        } else {
          handleApiError(responseData, dispatch);
        }
      } catch (err) {
        handleApiError(err, dispatch);
      }
    };
  };
